import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTypewriterPlaceholder } from "@/hooks/useTypewriterPlaceholder";

const NAME_SUGGESTIONS = [
  "Buzz Lightyear",
  "Hermione Granger",
  "SpongeBob",
  "Indiana Jones",
  "Bugs Bunny",
  "Marty McFly",
  "Lisa Simpson",
  "Frodo Baggins",
  "Scooby Doo",
  "Princess Leia",
  "Bart Simpson",
  "Mary Poppins",
  "Pikachu",
  "Forrest Gump",
  "Mickey Mouse",
  "Atticus Finch",
  "Dora",
  "James Bond",
  "Charlie Brown",
  "Willy Wonka",
];
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { NeoCard, NeoBadge } from "@/components/NeoCard";
import { SlotButton } from "@/components/SlotButton";
import {
  TIME_SLOTS,
  slotId,
  formatDayLabel,
  formatTimeLabel,
} from "@/lib/availability";
import { getApiErrorMessage, getApiErrorStatus, getMeeting, addResponse } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, CheckCircle, Lock, Users } from "lucide-react";

export default function Participant() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const nameInputRef = useRef(null);
  const namePlaceholder = useTypewriterPlaceholder(NAME_SUGGESTIONS, {
    inputRef: nameInputRef,
  });

  useEffect(() => {
    (async () => {
      try {
        const m = await getMeeting(id);
        setMeeting(m);
      } catch (e) {
        setError(getApiErrorMessage(e, "Could not load meeting"));
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const preferredSet = useMemo(
    () => new Set(meeting?.preferredSlots || []),
    [meeting]
  );

  const toggle = (sid) => {
    if (meeting?.status === "closed") return;
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(sid) ? next.delete(sid) : next.add(sid);
      return next;
    });
  };

  const submit = async () => {
    if (meeting?.status === "closed") {
      const message = "This meeting is closed and is no longer accepting responses.";
      setError(message);
      return toast.error(message);
    }
    if (!name.trim()) {
      setError("Enter your name before submitting.");
      return toast.error("Enter your name before submitting");
    }
    if (selected.size === 0) {
      setError("Pick at least one time slot.");
      return toast.error("Pick at least one time slot");
    }
    setSubmitting(true);
    setError("");
    try {
      await addResponse(id, { name: name.trim(), slots: [...selected] });
      toast.success("Availability saved");
      navigate(`/m/${id}/results`);
    } catch (e) {
      const message = getApiErrorMessage(e, "Could not save availability");
      if (getApiErrorStatus(e) === 400 && message.toLowerCase().includes("closed")) {
        setError("This meeting is closed and is no longer accepting responses.");
        toast.error("This meeting is closed");
      } else {
        setError(message);
        toast.error(message);
      }
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <NeoCard className="max-w-2xl w-full text-center">
        <p className="font-mono text-sm">Loading meeting...</p>
      </NeoCard>
    );
  }

  if (!meeting) {
    return (
      <NeoCard className="max-w-2xl w-full" data-testid="not-found-card">
        <NeoBadge variant="pink">NOT FOUND</NeoBadge>
        <h2 className="neo-heading text-3xl mt-3 mb-2">
          This meeting doesn't exist here.
        </h2>
        <p className="font-mono text-sm text-black/70 mb-6">
          Meetings created in a different browser may not be visible unless
          the API is running.
        </p>
        {error && <p className="font-mono text-sm text-[#b00020] mb-4">{error}</p>}
        <Link
          to="/"
          className="inline-block bg-black text-white border-[2px] border-black rounded-lg px-4 py-2 shadow-[3px_3px_0_0_#000] font-bold"
        >
          Create a new one
        </Link>
      </NeoCard>
    );
  }

  const days = [...(meeting.days || [])].sort();
  const timeSlots = meeting.timeSlots || TIME_SLOTS;
  const isClosed = meeting.status === "closed";
  const finalizedParts = meeting.finalizedSlot
    ? meeting.finalizedSlot.split("|")
    : [];
  const finalizedLabel =
    finalizedParts.length === 2
      ? `${formatDayLabel(finalizedParts[0])} at ${formatTimeLabel(finalizedParts[1])}`
      : meeting.finalizedSlot;

  return (
    <NeoCard className="max-w-3xl w-full" data-testid="participant-card">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <NeoBadge variant="cyan">
          <Users className="w-3 h-3" /> YOU'RE INVITED
        </NeoBadge>
        <Link
          to={`/m/${id}/results`}
          data-testid="link-to-results"
          className="font-mono text-xs underline underline-offset-4 hover:text-black/60"
        >
          See results ->
        </Link>
      </div>

      <h1 className="neo-heading text-3xl sm:text-4xl leading-[0.95] mb-1">
        {meeting.title}
      </h1>
      <p className="font-mono text-sm text-black/60 mb-6">
        {meeting.responseCount || 0} people have responded. Times shown in{" "}
        <span className="font-bold text-black">{meeting.timezone || "America/New_York"}</span>.
      </p>

      {isClosed && (
        <div className="border-[2px] border-black rounded-xl bg-[#fff9ed] shadow-[3px_3px_0_0_#000] p-4 mb-6">
          <div className="flex items-center gap-2 mb-1">
            {meeting.finalizedSlot ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <Lock className="w-5 h-5" />
            )}
            <h2 className="neo-heading text-xl">
              {meeting.finalizedSlot ? "Meeting finalized" : "Meeting closed"}
            </h2>
          </div>
          <p className="font-mono text-sm text-black/70">
            {meeting.finalizedSlot
              ? `The organizer picked ${finalizedLabel}.`
              : "The organizer is no longer collecting availability."}
          </p>
        </div>
      )}

      <label className="block font-bold text-xs uppercase tracking-wider mb-1.5">
        Your name
      </label>
      <Input
        ref={nameInputRef}
        data-testid="participant-name-input"
        value={name}
        onChange={(e) => setName(e.target.value)}
        disabled={isClosed}
        placeholder={namePlaceholder}
        className="border-[2px] border-black rounded-lg font-mono text-sm h-11 shadow-[3px_3px_0_0_#000] focus-visible:ring-0 focus-visible:shadow-[4px_4px_0_0_#000] mb-6"
      />

      <label className="block font-bold text-xs uppercase tracking-wider mb-2">
        Tap every slot you're available
      </label>
      <Accordion
        type="multiple"
        defaultValue={days.slice(0, 1)}
        className="space-y-3"
      >
        {days.map((day) => {
          const daySelectedCount = [...selected].filter((s) =>
            s.startsWith(day + "|")
          ).length;
          return (
            <AccordionItem
              key={day}
              value={day}
              className="border-[2px] border-black rounded-xl bg-[#fff9ed] overflow-hidden"
            >
              <AccordionTrigger
                className="px-4 py-3 hover:no-underline [&[data-state=open]]:bg-[#ffd60a]"
                data-testid={`day-trigger-${day}`}
              >
                <div className="flex items-center justify-between w-full pr-2">
                  <span className="neo-heading text-base text-left">
                    {formatDayLabel(day)}
                  </span>
                  <span className="font-mono text-[11px] text-black/60">
                    {daySelectedCount}/{timeSlots.length} picked
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4 pt-2">
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {timeSlots.map((t) => {
                    const sid = slotId(day, t);
                    return (
                      <SlotButton
                        key={sid}
                        time={t}
                        selected={selected.has(sid)}
                        preferred={preferredSet.has(sid)}
                        onClick={() => toggle(sid)}
                        disabled={isClosed}
                        testId={`slot-${sid}`}
                      />
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      <Button
        onClick={submit}
        disabled={submitting || isClosed}
        data-testid="submit-availability-btn"
        className="mt-6 w-full h-12 bg-black text-white hover:bg-black/90 border-[2px] border-black rounded-lg shadow-[4px_4px_0_0_#c4f04a] hover:-translate-y-[2px] hover:shadow-[6px_6px_0_0_#c4f04a] active:translate-y-[1px] active:shadow-[2px_2px_0_0_#c4f04a] font-bold uppercase tracking-wider"
      >
        {isClosed
          ? "Meeting closed"
          : submitting
          ? "Submitting..."
          : "Submit availability"}
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
      {error && (
        <p className="mt-3 font-mono text-sm text-[#b00020]" role="alert">
          {error}
        </p>
      )}
    </NeoCard>
  );
}
